/* eslint-disable eqeqeq */

import './SuggestionList.css';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Suggestion, SuggestionStatus, SuggestionStats, IsCompleted, IsRejected, IsOpen } from './Models';
import Loading from '../../components/Loading';
import { getFormattedDate } from '../../helpers';
import { Caste, User } from '../../App';
import { SearchOrderBy } from '../../endpoints';
import { UserContextMenu } from './UserContextMenu';
import Avatar from './Avatar';

interface Props {
    suggestions: Suggestion[];
    loaded: boolean;
    stats: SuggestionStats;
    user: User | null;
    status: SuggestionStatus | null;

    upvote: (id: number) => void;
    downvote: (id: number) => void;
    open: (id: number) => void;

    deleteSuggestion: (id: number) => void;
    rejectSuggestion: (id: number) => void;
    completeSuggestion: (id: number) => void;
    reopenSuggestion: (id: number) => void;

    onBanStateChange: (id: string, banState: boolean) => void;
    onAdminStateChange: (id: string, caste: Caste) => void;

    onSearch: (search: string | null, sort: number | null, status: SuggestionStatus | null) => void;
}

interface State {
    query: string | null;
    sort: SearchOrderBy | null;
}

export default class SuggestionList extends React.Component<Props & RouteComponentProps, State> {
    constructor(props: Props & RouteComponentProps) {
        super(props);
        this.state = {
            query: '',
            sort: SearchOrderBy.Score,
        };

        this.handleSearchInput = this.handleSearchInput.bind(this);
    }

    handleSearchInput(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({
            query: event.target.value
        });
    }

    setSearchState(status: SuggestionStatus | null) {
        if (this.props.status == status) {
            status = null;
        }

        this.props.onSearch(this.state.query, this.state.sort, status);
    }

    handleSearch() {
        this.props.onSearch(this.state.query, this.state.sort, this.props.status);
    }

    handleSortChange(sort: SearchOrderBy) {
        if (this.state.sort == sort) {
            return;
        }

        this.setState({
            sort
        });

        this.props.onSearch(this.state.query, sort, this.props.status);
    }

    onSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key == 'Enter') {
            this.handleSearch();
        }
    }

    RenderSuggestion: React.FC<{ sug: Suggestion }> = (props) => {
        const sug = props.sug;
        const upvoteClass = sug.isUserUpvoted
            ? 'vote-counter--upvoted'
            : (sug.isUserUpvoted == false ? 'vote-counter--downvoted' : '');

        const statusClass = (() => {
            switch (sug.status) {
                case 'OPEN':
                    return '';
                case 'COMPLETED':
                    return 'suggestion--completed';
                case 'REJECTED':
                default:
                    return 'suggestion--rejected';
            }
        })();

        const isAdmin = this.props.user?.caste == Caste.Admin;
        const isAuthor = this.props.user == null ? false : this.props.user.id == sug.author.id;

        return (<div className={'suggestion ' + statusClass} key={sug.id}>
            <div className={'vote-counter ' + upvoteClass}>
                <div className="vote-counter__up" onClick={() => this.props.upvote(sug.id)}>
                    <svg viewBox="0 0 24 24">
                        <path fill="#000000" d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" />
                    </svg>
                </div>
                <div className="vote-counter__count">{sug.score}</div>
                <div className="vote-counter__down" onClick={() => this.props.downvote(sug.id)}>
                    <svg viewBox="0 0 24 24">
                        <path fill="#000000" d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
                    </svg>
                </div>
            </div>
            <div className="suggestion__content">
                <div className="suggestion__title" onClick={() => this.props.open(sug.id)}>
                    <span className="suggestion__id">#{sug.id}</span>
                    {sug.title}
                </div>
                <div className="suggestion__subtitle">
                    <Avatar user={sug.author} />
                    <UserContextMenu
                        user={sug.author}
                        invoker={this.props.user}
                        onBanStateChange={this.props.onBanStateChange}
                        onAdminStateChange={this.props.onAdminStateChange}
                    >
                        <span className="suggestion__username">{sug.author.name}</span>
                    </UserContextMenu>
                    <span>{getFormattedDate(sug.createdAt)}</span>
                    <span>{sug.commentsCount} comment{sug.commentsCount == 1 ? '' : 's'}</span>
                    {sug.status != 'OPEN' && <span className="suggestion__status">{sug.status}</span>}
                </div>
            </div>
            <div className="suggestion__actions">
                {isAdmin && !IsOpen(sug) && <div
                    className="icon-button icon-button--blue"
                    onClick={() => this.props.reopenSuggestion(sug.id)}
                >
                    <svg viewBox="0 0 24 24">
                        <path fill="#000000" d="M12.5,8C9.85,8 7.45,9 5.6,10.6L2,7V16H11L7.38,12.38C8.77,11.22 10.54,10.5 12.5,10.5C16.04,10.5 19.05,12.81 20.1,16L22.47,15.22C21.08,11.03 17.15,8 12.5,8Z" />
                    </svg>
                </div>}
                {!IsCompleted(sug) && isAdmin && <div
                    className="icon-button icon-button--green"
                    onClick={() => this.props.completeSuggestion(sug.id)}
                >
                    <svg viewBox="0 0 24 24">
                        <path fill="#000000" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
                    </svg>
                </div>}
                {!IsRejected(sug) && (isAdmin || isAuthor) && <div
                    className="icon-button icon-button--yellow"
                    onClick={() => this.props.rejectSuggestion(sug.id)}
                >
                    <svg viewBox="0 0 24 24">
                        <path fill="#000000" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                    </svg>
                </div>}
                {isAdmin && <div
                    className="icon-button icon-button--red"
                    onDoubleClick={() => this.props.deleteSuggestion(sug.id)}
                >
                    <svg viewBox="0 0 24 24">
                        <path fill="#000000" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                    </svg>
                </div>}
            </div>
        </div>
        );
    }

    render() {
        return (
            <div className="suggestion-list">
                <div className="suggestion-list-header">
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="Search"
                            onChange={this.handleSearchInput}
                            value={this.state.query || ''}
                            onKeyDown={(ev) => this.onSearchKeyDown(ev)}
                        />
                        <div className="icon">
                            <svg viewBox="0 0 24 24">
                                <path fill="#000000" d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41
                                 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
                            </svg>
                        </div>
                    </div>
                    <div className="sort">
                        <span className="filter-option">Sort:</span>
                        <span className={'sort__by ' + (this.state.sort == SearchOrderBy.Score ? 'sort__by--selected' : '')}
                            onClick={() => this.handleSortChange(SearchOrderBy.Score)}
                        >
                            Votes
                        </span>
                        <span className={'sort__by ' + (this.state.sort == SearchOrderBy.Date ? 'sort__by--selected' : '')}
                            onClick={() => this.handleSortChange(SearchOrderBy.Date)}
                        >
                            Date
                        </span>
                    </div>
                    <div className="filter">
                        <span className="filter-option">Filter:</span>
                        <span
                            className={'filter__by ' + (this.props.status == 'OPEN' ? 'filter__by--selected' : '')}
                            onClick={() => this.setSearchState('OPEN')}>
                            Open
                        </span>
                        <span
                            className={'filter__by ' + (this.props.status == 'COMPLETED' ? 'filter__by--selected' : '')}
                            onClick={() => this.setSearchState('COMPLETED')}>
                            Completed
                        </span>
                        <span
                            className={'filter__by ' + (this.props.status == 'REJECTED' ? 'filter__by--selected' : '')}
                            onClick={() => this.setSearchState('REJECTED')}>
                            Rejected
                        </span>
                    </div>
                    <div className="suggestion-list-header__spacer"></div>
                    <div className="icon" onClick={() => this.handleSearch()}>
                        <svg viewBox="0 0 24 24">
                            <path fill="#000000" d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0
                             0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
                        </svg>
                    </div>
                </div>
                {!this.props.loaded
                    ? <Loading />
                    : this.props.suggestions.map((sug) => <this.RenderSuggestion key={sug.id} sug={sug} />)}
            </div>);
    }
}